import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Plus, Calendar, X } from 'lucide-react';
import { toast } from 'sonner';

export default function PortalLeavePage() {
  const [leaves, setLeaves] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ type: 'ANNUAL', start_at: '', end_at: '', reason: '' });
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try { const res = await api.get('/leave/my'); setLeaves(res.data.items || res.data.data || []); } catch (e) {}
  };
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post('/leave/request', { ...form, start_at: form.start_at + 'T00:00:00', end_at: form.end_at + 'T23:59:59' });
      toast.success('Leave requested'); setDialogOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const cancelLeave = async (id) => {
    try { await api.post(`/leave/${id}/cancel`); toast.success('Leave cancelled'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight">My Leave</h1><p className="text-muted-foreground mt-1">Request and manage your leave</p></div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <Button onClick={() => setDialogOpen(true)}><Plus className="h-4 w-4 mr-2" /> Request Leave</Button>
          <DialogContent>
            <DialogHeader><DialogTitle>Request Leave</DialogTitle><DialogDescription>Submit a leave request</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({...form, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['ANNUAL','PERSONAL','UNPAID','TRAINING','OTHER'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Start Date</Label><TrinitiDatePicker value={form.start_at} onChange={(v) => setForm({...form, start_at: v})} required testId="leave-start-at" /></div>
                <div className="space-y-2"><Label>End Date</Label><TrinitiDatePicker value={form.end_at} onChange={(v) => setForm({...form, end_at: v})} required testId="leave-end-at" /></div>
              </div>
              <div className="space-y-2"><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} /></div>
              <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Requesting...' : 'Submit Request'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {leaves.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No leave requests</TableCell></TableRow>
            : leaves.map(l => (
              <TableRow key={l.id}>
                <TableCell><StatusBadge status={l.type} /></TableCell>
                <TableCell className="font-mono text-xs">{l.start_at?.slice(0,10)}</TableCell>
                <TableCell className="font-mono text-xs">{l.end_at?.slice(0,10)}</TableCell>
                <TableCell className="text-sm">{l.reason || '-'}</TableCell>
                <TableCell><StatusBadge status={l.status} /></TableCell>
                <TableCell>
                  {['PENDING','APPROVED'].includes(l.status) && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => cancelLeave(l.id)}><X className="h-3 w-3 mr-1" /> Cancel</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
